import { useState } from "react";
import { Button, Card, ConfirmDialog, EmptyState, Spinner } from "../../shared/ui/index.js";
import { fmtDate } from "../../shared/format.js";
import { useToast } from "../../shared/toast.js";
import { InviteMemberDialog } from "./InviteMemberDialog.js";
import { useRemoveMember, useWeddingMembers } from "./weddings.api.js";

export function TeamPanel({ weddingId }: { weddingId: string }) {
  const { data, isLoading } = useWeddingMembers(weddingId);
  const remove = useRemoveMember(weddingId);
  const toast = useToast();
  const [invite, setInvite] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const target = data?.members.find((m) => m.id === confirmId);

  return (
    <Card id="team">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-lg text-stone-900">Équipe</h2>
          <p className="mt-1 text-sm text-stone-500">
            Les personnes ci-dessous peuvent se connecter et accéder à ce mariage et à tous ses
            événements.
          </p>
        </div>
        {data?.canManage && <Button onClick={() => setInvite(true)}>Donner accès</Button>}
      </div>

      {isLoading ? (
        <div className="mt-4">
          <Spinner />
        </div>
      ) : (
        <ul className="mt-4 flex flex-col gap-2">
          {data?.owner && (
            <li className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 bg-stone-50 px-4 py-3">
              <span className="min-w-0">
                <span className="block truncate font-medium text-stone-900">
                  {data.owner.name}
                </span>
                <span className="block truncate text-xs text-stone-500">{data.owner.email}</span>
              </span>
              <span className="shrink-0 rounded-full bg-stone-200 px-2.5 py-1 text-xs font-medium text-stone-600">
                Propriétaire
              </span>
            </li>
          )}
          {data?.members.map((m) => (
            <li
              key={m.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-stone-200 px-4 py-3"
            >
              <span className="min-w-0">
                <span className="block truncate font-medium text-stone-900">{m.name}</span>
                <span className="block truncate text-xs text-stone-500">
                  {m.email} · accès depuis le {fmtDate(m.createdAt)}
                </span>
              </span>
              {data.canManage && (
                <button
                  onClick={() => setConfirmId(m.id)}
                  className="shrink-0 text-xs font-medium text-rose-600 hover:underline"
                >
                  Retirer
                </button>
              )}
            </li>
          ))}
          {data?.members.length === 0 && (
            <EmptyState>
              Personne d'autre n'a accès pour l'instant — invitez un partenaire ou un collaborateur.
            </EmptyState>
          )}
        </ul>
      )}

      {invite && <InviteMemberDialog weddingId={weddingId} onClose={() => setInvite(false)} />}

      {target && (
        <ConfirmDialog
          title="Retirer cet accès ?"
          body={`${target.name} ne pourra plus se connecter à ce mariage. Son compte n'est pas supprimé.`}
          confirmLabel="Retirer l'accès"
          onConfirm={() =>
            remove.mutate(target.id, {
              onSuccess: () => toast("Accès retiré"),
              onError: () => toast("Échec", "err"),
            })
          }
          onClose={() => setConfirmId(null)}
        />
      )}
    </Card>
  );
}
